import type { SpriteDefinition } from '@/render/spriteBaker';
import { PALETTE } from '@/sprites/palette';
import { TILE_SIZE } from '@/sprites/tiles';

/**
 * Crop art.
 *
 * The first three growth stages are shared by every crop — a sprout is a
 * sprout — and only the ripe stage is drawn per plant. That keeps the art
 * budget for a dozen crops at a dozen sprites rather than fifty, and makes
 * "nearly ready" read consistently across the whole farm.
 */

/**
 * Bottom-align content in a 16x16 grid.
 *
 * Crops are drawn over a soil tile, so they must sit on the ground rather than
 * float in the middle of it. Padding here also removes a whole class of
 * off-by-one row-count mistakes from hand-authored art.
 */
function rooted(rows: readonly string[], bottomGap = 1): string[] {
  const padding = TILE_SIZE - rows.length - bottomGap;
  if (padding < 0) throw new Error(`Crop sprite is ${rows.length} rows, too tall for a tile`);
  const blank = '.'.repeat(TILE_SIZE);
  return [...Array.from({ length: padding }, () => blank), ...rows, ...Array.from({ length: bottomGap }, () => blank)];
}

const SEEDLING = rooted([
  '.......l........',
  '......lLl.......',
  '.......L........',
]);

const SPROUT = rooted([
  '.......ll.......',
  '......lLLl......',
  '.....llLLll.....',
  '......lLLl......',
  '.......LL.......',
]);

const BUSH = rooted([
  '......l..l......',
  '.....lLllLl.....',
  '....llLLLLll....',
  '...llLLLLLLll...',
  '....llLLLLll....',
  '.....lLLLLl.....',
  '......LLLL......',
  '.......LL.......',
]);

/** Ripe stages: the bush silhouette with the crop's own fruit showing. */
const TOMATO = rooted([
  '......l..l......',
  '.....lLllLl.....',
  '....llLzzLll....',
  '...llLzzzzLll...',
  '....lzzzzZZl....',
  '.....lzZZZl.....',
  '......LZZL......',
  '.......LL.......',
]);

const TURNIP = rooted([
  '......lUUl......',
  '.....lLUUll.....',
  '....llLuuLll....',
  '...lluuuuuull...',
  '....luuuuUUl....',
  '.....luUUUl.....',
  '......UUUU......',
  '.......LL.......',
]);

const POTATO = rooted([
  '......l..l......',
  '.....lLllLl.....',
  '....llLLLLll....',
  '...llLLLLLLll...',
  '....liiiiIIl....',
  '.....liiIIl.....',
  '......IIII......',
  '.......LL.......',
]);

const PUMPKIN = rooted([
  '......l..l......',
  '.....lLllLl.....',
  '....llLLLLll....',
  '...ccccccccc....',
  '..cccccccccCC...',
  '..ccccccccCCC...',
  '...cccccCCCC....',
  '....CCCCCCC.....',
]);

export const CROP_SPRITES = {
  seedling: { name: 'crop-seedling', rows: SEEDLING, palette: PALETTE },
  sprout: { name: 'crop-sprout', rows: SPROUT, palette: PALETTE },
  bush: { name: 'crop-bush', rows: BUSH, palette: PALETTE },
  tomatoRipe: { name: 'crop-tomato-ripe', rows: TOMATO, palette: PALETTE },
  turnipRipe: { name: 'crop-turnip-ripe', rows: TURNIP, palette: PALETTE },
  potatoRipe: { name: 'crop-potato-ripe', rows: POTATO, palette: PALETTE },
  pumpkinRipe: { name: 'crop-pumpkin-ripe', rows: PUMPKIN, palette: PALETTE },
} as const satisfies Record<string, SpriteDefinition>;

export type CropSpriteKey = keyof typeof CROP_SPRITES;
